import { getWebPath } from '@doss/common-all'
import { sortBy } from 'lodash'
import { ChevronDownIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { MenuDropdown } from '~/@/components/inputs/selection/menu-dropdown'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { SimpleProject } from '~/common/hooks/useOrgProjects'
import { CompanyLogo } from './CompanyLogo'
import { ProjectAvatar } from './ProjectAvatar'

type ProjectsDropdownProps = {
  projects: SimpleProject[]
  project?: SimpleProject
  setProject: (project: SimpleProject) => void
}

export function ProjectsDropdown({ projects, project, setProject }: ProjectsDropdownProps) {
  const navigate = useNavigate()
  const { dispatch } = useDossSignals()
  const sortedProjects = useMemo(() => sortBy(projects, (p) => p.order, 'asc'), [projects])
  const hasMultipleProjects = projects.length > 1

  const navigateToProjectHome = () => {
    navigate(
      getWebPath({
        objectId: project?.id,
        objectType: BreadcrumbObjectType.PROJECT,
      }),
    )
    dispatch(DossSignalType.TOGGLE_MOBILE_MENU, false)
  }

  const menuItems = useMemo(
    () =>
      sortedProjects.map((p, i) => ({
        label: p.name,
        icon: i === 0 ? <CompanyLogo project={p} small /> : <ProjectAvatar project={p} small />,
        onClick: () => {
          dispatch(DossSignalType.TOGGLE_MOBILE_MENU, false)
          setProject(p)
          navigate(
            getWebPath({
              objectId: p.id,
              objectType: BreadcrumbObjectType.PROJECT,
            }),
          )
        },
      })),
    [sortedProjects, dispatch, setProject, navigate],
  )

  const nameLabel = (
    <span className="whitespace-nowrap text-sm">{project?.name || 'No Projects'}</span>
  )

  return (
    <div className="flex items-center text-gray-10">
      {/* Project Logo - always navigates to project home */}
      <div
        className="flex cursor-pointer items-center rounded px-3 py-2 hover:bg-gray-2"
        onClick={navigateToProjectHome}
      >
        <div className="flex size-6 items-center justify-center">
          {project?.id === sortedProjects.at(0)?.id ? (
            <CompanyLogo project={project} />
          ) : (
            <ProjectAvatar project={project} />
          )}
        </div>
      </div>

      {/* Project Name + Chevron - opens dropdown if multiple projects */}
      {hasMultipleProjects ? (
        <MenuDropdown
          items={menuItems}
          menuClassName="w-56"
          trigger={
            <div className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-2">
              {nameLabel}
              <ChevronDownIcon className="size-4" />
            </div>
          }
        />
      ) : (
        <div className="flex items-center gap-2 rounded p-2">{nameLabel}</div>
      )}
    </div>
  )
}
